<script context="module" lang="ts">
  import { MeshBasicMaterial, MeshMatcapMaterial, Vector2 } from 'three';
  import { glassBeforeCompile } from './Glass_helpers';
  import { getSharedTextures } from './textures_helpers.svelte';

  type Materials = {
    [key: string]: {
      [key: string]: any;
    };
  };

  const getMaterials = async (textures: any, sections: string[]) => {
    const allMaterials: Materials = {};

    // reused materials
    const t2Glass = new MeshMatcapMaterial({
      color: 'white',
      transparent: true,
      matcap: textures.shared.t2Reflection,
      opacity: 0.2,
    });
    t2Glass.onBeforeCompile = glassBeforeCompile;

    const blackFrame = new MeshMatcapMaterial({
      matcap: textures.shared.blackFrame,
      color: '#666',
    });

    // loop through sections on the page and make materials needed for them
    sections.forEach((section) => {
      switch (section) {
        case 'register': {
          allMaterials.x2 = {
            silver: new MeshMatcapMaterial({
              matcap: textures.shared.liquidMetal,
              map: textures.register.x2Ao,
              normalMap: textures.shared.clearCoat,
              normalScale: new Vector2(0.2, 0.2),
            }),
            x2_screen_front: new MeshBasicMaterial({
              map: textures.register.x2ScreenFront,
            }),
            x2_screen_back: new MeshBasicMaterial({
              map: textures.register.x2ScreenBack,
            }),
            blackFrame,
          };
          break;
        }

        case 'terminal': {
          allMaterials.t2 = {
            whitePlastic: new MeshMatcapMaterial({
              matcap: textures.shared.whitePlastic,
              map: textures.terminal.t2Ao,
              normalMap: textures.terminal.t2Normals,
            }),
            t2Glass,
            t2_screen: new MeshBasicMaterial({
              map: textures.terminal.t2Screen,
            }),
            blackFrame,
          };
          break;
        }

        case 'stand': {
          const glassMaterial = new MeshMatcapMaterial({
            color: 'white',
            transparent: true,
            matcap: textures.shared.blackFrame,
          });
          glassMaterial.onBeforeCompile = glassBeforeCompile;

          allMaterials.s3 = {
            ipadBezel: new MeshMatcapMaterial({
              matcap: textures.shared.liquidMetal,
            }),

            glass: glassMaterial,
            s3_screen: new MeshBasicMaterial({
              map: textures.stand.s3Screen,
            }),
            s3_whitePlastic: new MeshMatcapMaterial({
              matcap: textures.shared.whitePlastic,
              map: textures.stand.s3Ao,
            }),
            s3_glow: new MeshMatcapMaterial({
              matcap: textures.shared.whitePlastic,
              color: 'white',
            }),
            blackFrame: new MeshMatcapMaterial({
              matcap: textures.shared.blackFrame,
              normalMap: textures.shared.clearCoat,
            }),
            blackOutline: blackFrame,
          };
          break;
        }

        case 'software': {
          const darkBlack = new MeshMatcapMaterial({
            matcap: textures.laptop.laptopDarkBlack,
            color: '#888',
          });

          allMaterials.laptop = {
            frame: new MeshMatcapMaterial({
              matcap: textures.laptop.laptopFrame,
              normalMap: textures.laptop.laptopFrameNormals,
              map: textures.laptop.laptopAo,
            }),

            blackKeys: new MeshMatcapMaterial({
              matcap: textures.laptop.laptopFrame,
              map: textures.laptop.laptopKeysMap,
            }),

            darkBlack,
            laptop_screen: new MeshBasicMaterial({
              map: textures.laptop.laptopScreen,
            }),
          };

          allMaterials.phone = {
            phone_screen: new MeshBasicMaterial({
              map: textures.phone.phoneScreen,
            }),
            brushedTitanium: new MeshMatcapMaterial({
              matcap: textures.phone.phone,
            }),
            phoneGlass: t2Glass,
            bezel: darkBlack,
          };
          break;
        }

        case 'banking': {
          allMaterials.card = {
            card_front: new MeshMatcapMaterial({
              matcap: textures.card.card,
              map: textures.card.cardFront,
              normalMap: textures.card.cardNormal,
              color: '#f3f3f3',
            }),
            card_front_metallic: new MeshMatcapMaterial({
              matcap: textures.card.cardReflection,
              alphaMap: textures.card.cardAlpha,
              transparent: true,
            }),
            card_back: new MeshMatcapMaterial({
              matcap: textures.card.card,
              map: textures.card.cardBack,
              normalMap: textures.shared.clearCoat,
              color: '#f3f3f3',
            }),
            card_back_metallic: new MeshMatcapMaterial({
              matcap: textures.card.cardReflection,
              alphaMap: textures.card.cardAlpha,
              color: '#fff',
              transparent: true,
            }),
          };
          break;
        }

        case 'handheld': {
          const t3Glass = new MeshMatcapMaterial({
            color: 'white',
            transparent: true,
            matcap: textures.handheld.t3Reflection,
            opacity: 0.3,
          });
          t3Glass.onBeforeCompile = glassBeforeCompile;

          allMaterials.t3 = {
            squareLogo: new MeshMatcapMaterial({
              matcap: textures.shared.whitePlastic,
              color: '#aaa',
            }),
            whitePlastic: new MeshMatcapMaterial({
              matcap: textures.shared.whitePlastic,
              map: textures.handheld.t3AO,
            }),
            t3_whiteDiffuse: new MeshMatcapMaterial({
              matcap: textures.handheld.t3_whiteDiffuse,
              map: textures.handheld.t3AO,
              color: '#f6f6f6',
            }),
            t3Glass,
            t3_screen: new MeshBasicMaterial({
              map: textures.handheld.t3Screen,
            }),
            cameraFront: new MeshBasicMaterial({
              color: '#000',
            }),
            button: new MeshMatcapMaterial({
              matcap: textures.handheld.button,
              color: '#fff',
            }),
            t3_cameras: new MeshBasicMaterial({
              map: textures.handheld.t3cameras,
            }),
            blackFrame,
          };
          break;
        }

        default: {
          break;
        }
      }
    });
    return allMaterials;
  };

  // materials organized by object
  export async function getSharedMaterials(matList: any, sections: string[]) {
    let textures;
    let materials;

    const materialGroups = sections.flatMap((key) => {
      if (key === 'banking') {
        return 'card';
      }
      if (key === 'software') {
        return ['laptop', 'phone'];
      }
      return key;
    });
    materialGroups.push('shared');

    try {
      textures = await getSharedTextures(matList, materialGroups);
      materials = await getMaterials(textures, sections);
    } catch (err) {
      console.error(err);
    }

    return materials;
  }
</script>
